import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Radio, X, Sparkles } from "lucide-react";

/**
 * First-visit + on-demand onboarding for clients on an Interactive gallery.
 *
 * Why this exists: Interactive galleries surface heart/comment/live-session
 * affordances that a first-time visitor (often a family member of the
 * primary client) won't recognise. The parent (PublicGallery) decides when
 * to open this — on first paint after the collaborator is identified, OR
 * when the visitor clicks the "?" pill in the interactive header.
 *
 * Controlled component: parent owns `open` + `onClose`. Persistence
 * (localStorage flag so the auto-open doesn't fire twice) is handled by
 * the parent so the same modal supports both auto and manual triggers.
 *
 * The styling is intentionally light on chrome and heavy on typography to
 * match the editorial gallery aesthetic — it should feel like a welcome
 * note from the photographer, not a SaaS feature tour.
 */
export default function InteractiveOnboardingModal({ open, onClose, role, accent }) {
  const accentColor = accent || "#C9A961";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-[95] bg-stone-900/60 backdrop-blur-sm"
            data-testid="interactive-onboarding-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.97 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="relative w-full max-w-md bg-[#FAF9F6] shadow-2xl pointer-events-auto"
              data-testid="interactive-onboarding-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="interactive-onboarding-title"
            >
              <button
                onClick={onClose}
                className="absolute right-3 top-3 p-1 text-stone-500 hover:text-stone-900 transition-colors"
                aria-label="Close"
                data-testid="interactive-onboarding-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="p-8 sm:p-9">
                <div className="flex items-center gap-2 mb-4">
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                    style={{ backgroundColor: `${accentColor}25` }}
                  >
                    <Sparkles className="w-3.5 h-3.5" style={{ color: accentColor }} />
                  </span>
                  <div className="text-[10px] tracking-[0.32em] uppercase text-stone-500">
                    Interactive gallery
                  </div>
                </div>

                <h2
                  id="interactive-onboarding-title"
                  className="font-serif text-3xl font-light tracking-tight text-stone-900 leading-tight"
                >
                  Welcome.<br />Make this gallery yours.
                </h2>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                  This isn&apos;t a slideshow — it&apos;s a conversation with your photographer.
                  Here&apos;s how to take part:
                </p>

                <ul className="mt-6 space-y-5">
                  <li className="flex gap-3.5" data-testid="onboarding-step-favorites">
                    <span
                      className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full"
                      style={{ backgroundColor: `${accentColor}20` }}
                    >
                      <Heart className="w-4 h-4" style={{ color: accentColor }} fill={accentColor} />
                    </span>
                    <div>
                      <div className="text-sm font-medium text-stone-900">Tap the heart on any photo</div>
                      <div className="text-xs text-stone-500 leading-relaxed mt-0.5">
                        Mark your favorites. The photographer sees who loved which shot, so the edits go to the ones that matter most.
                      </div>
                    </div>
                  </li>

                  <li className="flex gap-3.5" data-testid="onboarding-step-comments">
                    <span
                      className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full"
                      style={{ backgroundColor: `${accentColor}20` }}
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: accentColor }} />
                    </span>
                    <div>
                      <div className="text-sm font-medium text-stone-900">Leave a note on any photo</div>
                      <div className="text-xs text-stone-500 leading-relaxed mt-0.5">
                        Anything you&apos;d like remembered or retouched — write it directly on the image. Your photographer reads every comment.
                      </div>
                    </div>
                  </li>

                  {role === "primary" && (
                    <li className="flex gap-3.5" data-testid="onboarding-step-live">
                      <span
                        className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <Radio className="w-4 h-4" style={{ color: accentColor }} />
                      </span>
                      <div>
                        <div className="text-sm font-medium text-stone-900">Invite family to a live viewing</div>
                        <div className="text-xs text-stone-500 leading-relaxed mt-0.5">
                          Tap <em className="not-italic font-medium">Start live session</em> at the top to view together in real time — everyone&apos;s reactions and hearts appear instantly.
                        </div>
                      </div>
                    </li>
                  )}
                </ul>

                <button
                  onClick={onClose}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 h-12 bg-stone-900 text-[#F5EFE0] text-xs uppercase tracking-[0.28em] hover:opacity-90 transition-opacity"
                  data-testid="interactive-onboarding-dismiss"
                >
                  Let&apos;s explore
                </button>
                <p className="mt-3 text-center text-[10px] tracking-[0.22em] uppercase text-stone-400">
                  Reopen anytime from the &ldquo;?&rdquo; in the header
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
